import type { VaultClaim, VaultDocument, VaultSku, VaultStatus } from '@/lib/api/vault';

/**
 * The funnel's pure state machine: every visitor-facing decision of docs/behavior/funnel.md §4–§5,
 * free of React, timers and the network so it can be tested exhaustively.
 */

/** A rung the visitor has earned, in ladder order (feeds the nav rail and the summon overlay). */
export interface RungLink {
  readonly sku: 'shadow-king' | 'shadow-oracle' | 'omega-os';
  readonly title: string;
  readonly latin: string;
  /** What the nav shows: the Latin name, or the title when the server sends none. */
  readonly label: string;
  /** Destination path; static surfaces end with a slash and are full-page links. */
  readonly href: string;
  /** False while the rung's deliverable does not exist yet (rendered `omega-nav--unforged`). */
  readonly forged: boolean;
}

/** Earned rungs, in this order. The Sovereign Stack is a permanent nav item, never a rung. */
export const NAV_ORDER = ['shadow-king', 'shadow-oracle', 'omega-os'] as const;

export function vaultDocument(status: VaultStatus | null, sku: VaultSku): VaultDocument | null {
  return status?.documents[sku] ?? null;
}

/**
 * Everything the funnel UI can look like reduced to one comparable string (§4.3):
 * `signedIn|tier|<sku><revealed><canDownload><owned><downloaded>…` in the server's key order.
 */
export function signature(status: VaultStatus | null): string {
  if (status === null) return '';
  const bit = (value: boolean | undefined) => (value === true ? '1' : '0');
  const parts = [bit(status.signedIn), status.tier !== null && status.tier !== '' ? status.tier : '-'];
  for (const [sku, doc] of Object.entries(status.documents)) {
    parts.push(sku + bit(doc?.revealed) + bit(doc?.canDownload) + bit(doc?.owned) + bit(doc?.downloaded));
  }
  return parts.join('|');
}

/** §5.5: revealed rungs in ladder order; the server's `revealed` alone decides, not sign-in. */
export function revealedRungs(status: VaultStatus | null): RungLink[] {
  const rungs: RungLink[] = [];
  for (const sku of NAV_ORDER) {
    const doc = vaultDocument(status, sku);
    if (doc === null || !doc.revealed) continue;
    rungs.push({
      sku,
      title: doc.title,
      latin: doc.latin,
      label: doc.latin !== '' ? doc.latin : doc.title,
      href: doc.route,
      forged: doc.forged,
    });
  }
  return rungs;
}

/**
 * §5.1 Sovereign Stack panel:
 * - `pending`: no usable status yet (or no sovereign-stack entry) — the capture form stays;
 * - `signed-out` (SS-A), `ready` (SS-B, signed in, not downloaded), `downloaded` (SS-C).
 */
export type StackPanelState = 'pending' | 'signed-out' | 'ready' | 'downloaded';

export function stackPanelState(status: VaultStatus | null): StackPanelState {
  const doc = vaultDocument(status, 'sovereign-stack');
  if (status === null || doc === null) return 'pending';
  if (!status.signedIn) return 'signed-out';
  return doc.downloaded ? 'downloaded' : 'ready';
}

/** §5.2.3 label of every Shadow King purchase/download control. */
export function shadowKingLabel(owned: boolean, price: string): string {
  if (owned) return 'DOWNLOAD THE JOURNAL';
  return price === '' ? 'TAKE THE JOURNAL' : `TAKE THE JOURNAL — ${price}`;
}

/** §5.2.3 per-box memo key, rendered as `data-omega-sksig`: signed-in bit, owned bit, price. */
export function priceBoxSignature(signedIn: boolean, owned: boolean, price: string): string {
  return `${signedIn ? 1 : 0}${owned ? 1 : 0}${price}`;
}

/** What a Shadow King purchase control does when pressed (§5.2.3 skAction, §5.4.1 step 1). */
export type ShadowKingAction = 'download' | 'sign-in' | 'waitlist' | 'checkout';

export function shadowKingAction(input: {
  readonly owned: boolean;
  readonly signedIn: boolean;
  readonly forged: boolean;
}): ShadowKingAction {
  if (input.owned) return 'download';
  if (!input.signedIn) return 'sign-in';
  return input.forged ? 'checkout' : 'waitlist';
}

/** Scientia's ladder CTA (measure-legal-auth §4.1): shown only to a signed-in visitor whose Shadow King rung is revealed. */
export function scientiaShadowKingRevealed(status: VaultStatus | null): boolean {
  return status !== null && status.signedIn && vaultDocument(status, 'shadow-king')?.revealed === true;
}

/**
 * FN-D1: a claim body replaces the ladder and tier but carries no `signedIn`/`firstName`; those
 * are kept from the status read. A successful claim is only ever answered to an authenticated
 * reader, so with no prior status the reader is signed in.
 */
export function mergeClaim(previous: VaultStatus | null, claim: VaultClaim): VaultStatus {
  return {
    success: true,
    signedIn: previous?.signedIn ?? true,
    firstName: previous?.firstName ?? null,
    tier: claim.tier,
    documents: claim.documents,
  };
}

/* ── Return from checkout (§5.4.2) ───────────────────────────────────── */

/** Marker written immediately before the checkout hand-off (shared with the static rung pages). */
export interface CheckoutPending {
  readonly sku: string;
  readonly at: number;
}

export const CHECKOUT_PENDING_MAX_AGE_MS = 6 * 60 * 60 * 1000;
export const CLAIM_MAX_ATTEMPTS = 12;

export function serializeCheckoutPending(sku: string, now: number): string {
  return JSON.stringify({ sku, at: now });
}

/**
 * Reads the stored marker. `expired` tells the caller to remove it (older than 6 h, or with no
 * timestamp). An unreadable marker or one without a sku is ignored and left in place.
 */
export function parseCheckoutPending(
  raw: string | null,
  now: number,
): { readonly pending: CheckoutPending | null; readonly expired: boolean } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw ?? 'null');
  } catch {
    return { pending: null, expired: false };
  }
  if (typeof parsed !== 'object' || parsed === null) return { pending: null, expired: false };
  const { sku, at } = parsed as { sku?: unknown; at?: unknown };
  if (typeof sku !== 'string' || sku === '') return { pending: null, expired: false };
  const stamp = typeof at === 'number' && Number.isFinite(at) ? at : 0;
  if (now - stamp > CHECKOUT_PENDING_MAX_AGE_MS) return { pending: null, expired: true };
  return { pending: { sku, at: stamp }, expired: false };
}

/** Wait before the next claim attempt: 2.5 s after attempts 1–3, then 6 s. */
export function claimDelayAfter(attempt: number): number {
  return attempt < 4 ? 2500 : 6000;
}
